import { Alert, AlertDescription, AlertIcon, Box, Button as ChakraButton, HStack, IconButton, Input, Slider, SliderFilledTrack, SliderThumb, SliderTrack, Spinner, VStack } from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FaCamera, FaCheck, FaRedo, FaUndo, FaUpload } from 'react-icons/fa';
import { Text } from '../../../../components/atoms/Text';
import { Button } from '../../../../components/atoms/Button';
import { useTheme } from '../../../../contexts/ThemeProvider';
import { validateCpfWithPhoto } from '../../../../service/telemedicine/api';
import { masks } from '../../../../utils/masks';
import { getCropAreaFromTransform, getCroppedImageBlob, rotateImageDataUrl } from '../../utils/cropImage';
import { CameraCapture } from '../CameraCapture';

const CROP_WIDTH = 280;
const CROP_HEIGHT = 176;
const VIEWPORT_SIZE = 320;

type Step = 'choose' | 'camera' | 'edit' | 'validating' | 'success';

interface DocumentCaptureStepProps {
  cpf: string;
  patientKey: string;
  onValidated: () => void;
}

export function DocumentCaptureStep({ cpf, patientKey, onValidated }: DocumentCaptureStepProps) {
  const { colors } = useTheme();
  const inputRef = useRef<HTMLInputElement>(null);
  const dragStartRef = useRef<{ x: number; y: number; posX: number; posY: number } | null>(null);

  const [step, setStep] = useState<Step>('choose');
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isRotating, setIsRotating] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const loadImage = useCallback((dataUrl: string) => {
    const image = new Image();
    image.onload = () => {
      setImageSize({ width: image.width, height: image.height });
      setImageSrc(dataUrl);
      setZoom(1);
      setPosition({ x: 0, y: 0 });
      setStep('edit');
      setErrorMessage('');
    };
    image.src = dataUrl;
  }, []);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => loadImage(reader.result as string);
    reader.readAsDataURL(file);
    event.target.value = '';
  };

  const handleRotate = async (degrees: 90 | -90) => {
    if (!imageSrc || isRotating) return;
    setIsRotating(true);
    try {
      const rotated = await rotateImageDataUrl(imageSrc, degrees);
      setImageSrc(rotated.dataUrl);
      setImageSize({ width: rotated.width, height: rotated.height });
      setZoom(1);
      setPosition({ x: 0, y: 0 });
    } finally {
      setIsRotating(false);
    }
  };

  const displayedImageStyle = useMemo(() => {
    if (!imageSize.width || !imageSize.height) return {};

    const baseScale = Math.max(CROP_WIDTH / imageSize.width, CROP_HEIGHT / imageSize.height);
    const scale = baseScale * zoom;

    return {
      width: `${imageSize.width * scale}px`,
      height: `${imageSize.height * scale}px`,
      transform: `translate(calc(-50% + ${position.x}px), calc(-50% + ${position.y}px))`,
    };
  }, [imageSize.height, imageSize.width, position.x, position.y, zoom]);

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (step !== 'edit') return;
    dragStartRef.current = { x: event.clientX, y: event.clientY, posX: position.x, posY: position.y };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragStartRef.current || step !== 'edit') return;
    setPosition({
      x: dragStartRef.current.posX + (event.clientX - dragStartRef.current.x),
      y: dragStartRef.current.posY + (event.clientY - dragStartRef.current.y),
    });
  };

  const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    dragStartRef.current = null;
    event.currentTarget.releasePointerCapture(event.pointerId);
  };

  const handleValidate = async () => {
    if (!imageSrc) return;

    setStep('validating');
    setErrorMessage('');

    try {
      const cropArea = getCropAreaFromTransform({
        imageWidth: imageSize.width,
        imageHeight: imageSize.height,
        containerWidth: VIEWPORT_SIZE,
        containerHeight: VIEWPORT_SIZE,
        cropWidth: CROP_WIDTH,
        cropHeight: CROP_HEIGHT,
        zoom,
        position,
      });

      const croppedPhoto = await getCroppedImageBlob(imageSrc, cropArea);
      const response = await validateCpfWithPhoto({ cpf, patientKey, photo: croppedPhoto });

      if (!response.success) {
        setStep('edit');
        setErrorMessage(response.message || 'Não foi possível validar o documento.');
        return;
      }

      setStep('success');
      window.setTimeout(() => onValidated(), 900);
    } catch {
      setStep('edit');
      setErrorMessage('Não foi possível processar a foto. Tente novamente.');
    }
  };

  useEffect(() => {
    return () => {
      dragStartRef.current = null;
    };
  }, []);

  const maskedCpf = useMemo(() => (cpf ? masks.cpf(cpf) : ''), [cpf]);

  return (
    <VStack align={'stretch'} spacing={3} width={'100%'}>
      <VStack align={'stretch'} spacing={1}>
        <Text fontSize={'15px'} color={'#4A5568'} lineHeight={'1.5'}>
          Envie uma foto nítida do documento com o CPF{' '}
          <Text as="span" fontWeight={700} color={'#292927'}>
            {maskedCpf}
          </Text>
          .
        </Text>
        <Text fontSize={'15px'} color={'#4A5568'} lineHeight={'1.5'}>
          Tire a foto agora pela câmera ou envie um arquivo — depois é só ajustar o recorte.
        </Text>
      </VStack>

      <Input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        display="none"
        onChange={handleFileChange}
      />

      {step === 'choose' && (
        <HStack spacing={3} width={'100%'} flexWrap={'wrap'}>
          <Button flex={1} minWidth={'200px'} leftIcon={<FaCamera />} onClick={() => setStep('camera')}>
            Usar câmera
          </Button>
          <Button
            flex={1}
            minWidth={'200px'}
            variant={'outline'}
            leftIcon={<FaUpload />}
            backgroundColor={'transparent'}
            color={colors.primaryColor}
            border={'1px solid'}
            borderColor={colors.primaryColor}
            _hover={{ backgroundColor: '#F7F9FA' }}
            onClick={() => inputRef.current?.click()}
          >
            Enviar arquivo
          </Button>
        </HStack>
      )}

      {step === 'camera' && (
        <CameraCapture onCapture={loadImage} onCancel={() => setStep(imageSrc ? 'edit' : 'choose')} />
      )}

      {step === 'edit' && imageSrc && (
        <VStack spacing={3} align={'stretch'}>
          <Box
            position={'relative'}
            width={`${VIEWPORT_SIZE}px`}
            maxWidth={'100%'}
            height={`${VIEWPORT_SIZE}px`}
            margin={'0 auto'}
            borderRadius={'16px'}
            overflow={'hidden'}
            backgroundColor={'#1A202C'}
            cursor={'grab'}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerLeave={handlePointerUp}
            style={{ touchAction: 'none' }}
          >
            {/* <img> real em vez de background-image: evita a tela ficar preta quando o
                background falha em carregar/dimensionar e permite feedback de loading nativo */}
            <img
              src={imageSrc}
              alt="Documento para recorte"
              draggable={false}
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                objectFit: 'fill',
                userSelect: 'none',
                pointerEvents: 'none',
                ...displayedImageStyle,
              }}
            />
            <Box
              position={'absolute'}
              top={'50%'}
              left={'50%'}
              width={`${CROP_WIDTH}px`}
              height={`${CROP_HEIGHT}px`}
              transform={'translate(-50%, -50%)'}
              border={'2px solid white'}
              borderRadius={'12px'}
              boxShadow={'0 0 0 9999px rgba(0, 0, 0, 0.45)'}
              pointerEvents={'none'}
            />
          </Box>

          <HStack justify={'center'} spacing={3}>
            <IconButton
              aria-label="Rotacionar para a esquerda"
              icon={<FaUndo />}
              variant={'outline'}
              borderRadius={'full'}
              isLoading={isRotating}
              onClick={() => handleRotate(-90)}
            />
            <IconButton
              aria-label="Rotacionar para a direita"
              icon={<FaRedo />}
              variant={'outline'}
              borderRadius={'full'}
              isLoading={isRotating}
              onClick={() => handleRotate(90)}
            />
          </HStack>

          <Box>
            <Text fontSize={'13px'} fontWeight={600} marginBottom={2}>
              Ajustar zoom
            </Text>
            <Slider min={1} max={3} step={0.05} value={zoom} onChange={setZoom}>
              <SliderTrack>
                <SliderFilledTrack backgroundColor={colors.primaryColor} />
              </SliderTrack>
              <SliderThumb />
            </Slider>
          </Box>

          {errorMessage && (
            <Alert status={'error'} borderRadius={'md'} alignItems={'flex-start'}>
              <AlertIcon />
              <VStack align={'stretch'} spacing={1} flex={1}>
                <AlertDescription fontSize={'13px'}>{errorMessage}</AlertDescription>
                <ChakraButton
                  alignSelf={'flex-start'}
                  variant={'link'}
                  size={'sm'}
                  color={'#C53030'}
                  fontWeight={600}
                  onClick={() => {
                    setStep('choose');
                    setImageSrc(null);
                    setErrorMessage('');
                  }}
                >
                  Revisar foto
                </ChakraButton>
              </VStack>
            </Alert>
          )}

          <HStack spacing={3}>
            <IconButton
              aria-label="Tirar foto novamente"
              icon={<FaCamera />}
              variant={'outline'}
              onClick={() => setStep('camera')}
            />
            <IconButton
              aria-label="Enviar foto novamente"
              icon={<FaUpload />}
              variant={'outline'}
              onClick={() => inputRef.current?.click()}
            />
            <Button flex={1} onClick={handleValidate}>
              Validar documento
            </Button>
          </HStack>
        </VStack>
      )}

      {step === 'validating' && (
        <VStack spacing={3} paddingY={4}>
          <Spinner color={colors.primaryColor} size={'lg'} />
          <Text fontSize={'14px'} color={'#4A5568'}>
            Validando documento enviado...
          </Text>
        </VStack>
      )}

      {step === 'success' && (
        <VStack spacing={3} paddingY={4}>
          <Box
            width={'48px'}
            height={'48px'}
            borderRadius={'full'}
            backgroundColor={'#C6F6D5'}
            display={'flex'}
            alignItems={'center'}
            justifyContent={'center'}
            color={'#22543D'}
          >
            <FaCheck />
          </Box>
          <Text fontSize={'16px'} fontWeight={700}>
            Documento validado com sucesso
          </Text>
          <Text fontSize={'14px'} color={'#4A5568'}>
            Seguindo para o agendamento...
          </Text>
        </VStack>
      )}
    </VStack>
  );
}
